import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { FilterFacet } from '@apps/stays/components/filters/FilterFacet';
import { StyledCheckboxV2 } from '@apps/stays/components/filters/StyledCheckboxV2';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useListingsPanelTelemetry } from '../../ListingsPanel/ListingsPanel.telemetry';
import { AccommodationCategory } from '@graphql/generated-base';
import { usePopularFilterOptions, VERY_GOOD_RATING } from '../../../hooks/usePopularFilterOptions';
import type { PopularFilterOption } from '../../../hooks/usePopularFilterOptions';
import type { Filters } from '@shared/types/Accommodations.types';

type PopularFiltersFacetProps = Readonly<{
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}>;

export const PopularFiltersFacet: React.FC<PopularFiltersFacetProps> = ({ filters, onFiltersChange }) => {
  const { tq } = useTranslation('accommodationsConsole');
  const telemetry = useListingsPanelTelemetry();
  const options = usePopularFilterOptions();

  const handleToggle = useEventCallback((option: PopularFilterOption) => {
    const wasActive = option.isActive(filters);
    const action = wasActive ? 'removed' : 'added';
    switch (option.filterType) {
      case 'cug':
        telemetry.joyTravelBenefitsFilterChanged({ action, popular: true });
        break;
      case 'userRating':
        telemetry.guestRatingFilterChanged({ rating: VERY_GOOD_RATING, action, popular: true });
        break;
      case 'facilities':
        telemetry.facilityFilterChanged({ facility: option.getLabel(), action, popular: true });
        break;
      case 'propertyCategory':
        telemetry.propertyCategoryFilterChanged({ propertyCategory: AccommodationCategory.Hotel, action, popular: true });
        break;
      case 'freeCancellation':
        telemetry.freeCancellationFilterChanged({ action, popular: true });
        break;
    }
    onFiltersChange(option.toggle(filters));
  });

  return (
    <FilterFacet title={tq`Popular Filters`}>
      <Flex
        flexDirection="column"
        gap={5}
      >
        {options.map(option => (
          <StyledCheckboxV2
            key={option.id}
            isChecked={option.isActive(filters)}
            onChange={() => handleToggle(option)}
            id={`popular-filter-${option.id}`}
          >
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {option.getLabel()}
            </TextV2>
          </StyledCheckboxV2>
        ))}
      </Flex>
    </FilterFacet>
  );
};
